import { z } from 'zod';

export const memberFormSchema = z
  .object({
    memberType: z.enum(['director', 'corporation'], {
      required_error: 'Please select a member type',
    }),
    directorId: z.string().nullable(),
    corporationId: z.string().nullable(),
    isAddingNewMember: z.boolean(),
    newMember: z
      .object({
        name: z.string().min(1, 'Name is required'),
        email: z.string().email('Please enter a valid email address'),
      })
      .nullable(),
    capitalContribution: z.object({
      amount: z.number({
        required_error: 'Capital contribution amount is required',
        invalid_type_error: 'Please enter a valid amount',
      }),
      currency: z.string().min(1, 'Currency is required'),
    }),
    membershipInterestPercentage: z
      .number({
        required_error: 'Membership interest percentage is required',
        invalid_type_error: 'Please enter a valid percentage',
      })
      .min(0, 'Membership interest percentage must be at least 0')
      .max(100, 'Membership interest percentage cannot exceed 100'),
    profitContributionPercentage: z
      .number({
        required_error: 'Profit contribution percentage is required',
        invalid_type_error: 'Please enter a valid percentage',
      })
      .min(0, 'Profit contribution percentage must be at least 0')
      .max(100, 'Profit contribution percentage cannot exceed 100'),
    lossContributionPercentage: z
      .number({
        required_error: 'Loss contribution percentage is required',
        invalid_type_error: 'Please enter a valid percentage',
      })
      .min(0, 'Loss contribution percentage must be at least 0')
      .max(100, 'Loss contribution percentage cannot exceed 100'),
    numberOfVotingRights: z.number({
      required_error: 'Number of voting rights is required',
      invalid_type_error: 'Please enter a valid number',
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.isAddingNewMember) {
      if (data.memberType === 'director' && !data.directorId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a person',
          path: ['directorId'],
        });
      } else if (data.memberType === 'corporation' && !data.corporationId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select an entity',
          path: ['corporationId'],
        });
      }
    } else {
      if (!data.newMember) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'New member information is required',
          path: ['newMember'],
        });
      } else {
        if (!data.newMember.name) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Name is required for new member',
            path: ['newMember', 'name'],
          });
        }
        if (!data.newMember.email) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Email is required for new member',
            path: ['newMember', 'email'],
          });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.newMember.email)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please enter a valid email address',
            path: ['newMember', 'email'],
          });
        }
      }
    }
  });

export type MemberFormFields = z.infer<typeof memberFormSchema>;
